import { useState } from 'react';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于汗汗漫画',
    description: '汗汗漫画是专业的免费韩国漫画在线阅读平台，提供海量优质韩漫资源。',
    url: 'https://www.hanhanmanga.com/about',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '汗汗漫画',
    url: 'https://www.hanhanmanga.com',
    description: '免费韩国漫画在线阅读平台',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@hanhanmanga.com',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '上海',
      addressCountry: 'CN',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '汗汗漫画是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，汗汗漫画提供完全免费的韩国漫画在线阅读服务。您可以无需注册即可浏览和阅读所有漫画内容。',
        },
      },
      {
        '@type': 'Question',
        name: '汗汗漫画的漫画多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们保持与韩国官方同步更新，大多数热门漫画每周更新一次，部分作品会根据作者进度调整更新频率。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在汗汗漫画上搜索想看的漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以使用网站顶部的搜索框输入漫画名称进行搜索，也可以通过分类页面按类型浏览漫画。我们支持按热度、评分和更新时间排序。',
        },
      },
      {
        '@type': 'Question',
        name: '汗汗漫画支持手机阅读吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '汗汗漫画采用响应式设计，完美适配手机、平板和电脑等多种设备。无论您使用什么设备，都能获得流畅的漫画阅读体验。',
        },
      },
    ],
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    try {
      const submitAddr = 'https://readdy.ai/api/form/d9p0r92qs38sj6jc7h80';
      const formData = new FormData(form);
      formData.delete('website_alt');

      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查您的网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEO
        title="关于我们 - 品牌介绍与联系方式"
        description="汗汗漫画是专业的免费韩国漫画在线阅读平台。了解我们的品牌故事、用户口碑和联系方式。提供海量优质韩漫资源，每日更新，访问流畅。"
        keywords="汗汗漫画,关于汗汗漫画,漫画网站,韩国漫画平台,联系汗汗漫画"
        canonicalPath="/about"
        ogType="website"
        schema={{ ...aboutSchema, ...orgSchema, ...faqSchema }}
      />
      <div className="px-4 md:px-6 pt-6">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <section className="mb-12">
            <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
              关于汗汗漫画
            </h1>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
              <div className="max-w-3xl">
                <h2 className="text-lg font-bold font-heading text-foreground-800 mb-3">品牌介绍</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  汗汗漫画成立于2020年，是一家专注于韩国漫画在线阅读的专业平台。我们的使命是让每一位漫画爱好者都能轻松找到自己喜欢的韩漫作品，享受高质量的阅读体验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  作为国内领先的<strong>免费韩国漫画</strong>平台，汗汗漫画拥有海量的<strong>韩漫在线</strong>资源库，涵盖恋爱、奇幻、热血、剧情、搞笑、悬疑、日常、BL等多种类型。无论您是韩漫老粉还是新入坑的读者，都能在这里找到心仪的作品。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们与韩国各大漫画平台保持密切合作，确保资源更新及时、内容全面。汗汗漫画采用先进的服务器架构，保障<strong>看漫画网站</strong>的访问流畅度，让您的阅读不受任何干扰。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  汗汗漫画团队由一群热爱漫画的年轻人组成，我们理解每一位读者的需求。未来我们将继续优化产品体验，引入更多优质漫画内容，成为您休闲时光的<strong>可靠伴侣</strong>。
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              <div className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  "一直在汗汗漫画看韩漫，更新速度很快，画质也很清晰。推荐给了很多朋友，大家都说好用！"
                </p>
                <p className="text-xs text-foreground-400">—— 漫画迷小王</p>
              </div>
              <div className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  "分类很全，想看的韩漫基本都能找到。手机上看也很方便，每天通勤路上必刷！"
                </p>
                <p className="text-xs text-foreground-400">—— 上班族小李</p>
              </div>
              <div className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  "页面设计简洁大方，没有乱七八糟的广告，阅读体验很棒。是我用过最好的免费漫画网站。"
                </p>
                <p className="text-xs text-foreground-400">—— 学生小张</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4">常见问题</h2>
            <div className="space-y-3 max-w-3xl">
              {[
                { q: '汗汗漫画是免费的吗？', a: '是的，汗汗漫画提供完全免费的韩国漫画在线阅读服务。您可以无需注册即可浏览和阅读所有漫画内容。' },
                { q: '漫画多久更新一次？', a: '我们保持与韩国官方同步更新，大多数热门漫画每周更新一次，部分作品会根据作者进度调整更新频率。' },
                { q: '如何搜索想看的漫画？', a: '您可以使用网站顶部的搜索框输入漫画名称进行搜索，也可以通过分类页面按类型浏览漫画。我们支持按热度、评分和更新时间排序。' },
                { q: '支持手机阅读吗？', a: '汗汗漫画采用响应式设计，完美适配手机、平板和电脑等多种设备，随时随地享受流畅的漫画阅读体验。' },
              ].map((item, idx) => (
                <details key={idx} className="group rounded-lg bg-background-50 border border-background-200/70">
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors">
                    {item.q}
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section className="pb-12">
            <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-500">contact@hanhanmanga.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-customer-service-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">在线客服</h3>
                    <p className="text-sm text-foreground-500">工作日 9:00 - 18:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-500">中国·上海</p>
                  </div>
                </div>
              </div>

              <div>
                <form
                  id="contact-form"
                  data-readdy-form="true"
                  onSubmit={handleSubmit}
                  className="p-5 rounded-lg bg-background-50 border border-background-200/70 space-y-4"
                >
                  <h3 className="text-sm font-semibold text-foreground-800">发送消息</h3>

                  <div className="honeypot-field" aria-hidden="true">
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly />
                  </div>

                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="请输入您的消息（500字以内）"
                      maxLength={500}
                      rows={4}
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-center text-green-600">消息发送成功！感谢您的联系，我们会尽快回复。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-center text-red-500">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}